import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { InBrowserModelService } from '../services/in-browser-model.service';
import { TranslateService } from '../i18n/translate.service';
import * as THREE from 'three';

interface KnowledgeEntry {
  id: string;
  topic: string;
  keywords: string[];
  text: string;
  textIt?: string;
  textEn?: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  title = 'Bompadre';
  name = 'Alessandro Bompadre';
  bio = 'A highly motivated and ambitious individual.';
  linkedin = 'https://www.linkedin.com';
  email = 'alessandro.bompadre@gmail.com';

  @ViewChild('modelContainer', { static: true }) modelContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('mainInput', { static: false }) mainInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('messageInput', { static: false }) messageInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('sideInput', { static: false }) sideInputRef?: ElementRef<HTMLInputElement>;

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private animId = 0;
  private mixer?: THREE.AnimationMixer;
  private availableClips?: THREE.AnimationClip[];
  private roleClips: { idle?: THREE.AnimationClip; talk?: THREE.AnimationClip; think?: THREE.AnimationClip } = {};
  private idleClip?: THREE.AnimationClip;
  private talkClip?: THREE.AnimationClip;
  private thinkClip?: THREE.AnimationClip;
  private idleAction?: THREE.AnimationAction;
  private talkAction?: THREE.AnimationAction;
  private thinkAction?: THREE.AnimationAction;
  private activeAction?: THREE.AnimationAction;
  private avatarModel?: THREE.Object3D;
  private materialDiagnosticsPrinted = false;
  private avatarBaseRotation?: THREE.Euler;
  private thinkMotionStart = 0;
  public avatarMotionState: 'none' | 'idle' | 'think' | 'talk' = 'none';
  private popInActive = false;
  private popInStart = 0;
  private popInDuration = 700; // ms
  private popInFromScale = new THREE.Vector3(0.001, 0.001, 0.001);
  private popInToScale = new THREE.Vector3(1, 1, 1);
  private lastTime = performance.now();
  // Fade (canvas opacity)
  private fadeActive = false;
  private fadeStart = 0;
  private fadeDuration = 500; // ms
  private fadeFrom = 0;
  private fadeTo = 1;
  private mouthMorphMeshes: THREE.Mesh[] = [];
  private eyeMeshes: Array<{mesh: THREE.Mesh, initialQuat: THREE.Quaternion}> = [];
  private speaking = false;
  private useMorphSpeaking = false;
  private speakStart = 0;
  private speakEnd = 0;
  private ttsSessionId = 0;
  private resizeObserver?: any;
  private defaultSpeakDuration = 1.8;
  // simple chat/messages state
  public messages: Array<{from: 'user' | 'bot', text: string}> = [];
  private readonly chatMemoryTurns = 8;

  // generic fallback used only when model output is empty/unavailable
  private readonly botReply = 'Posso aiutarti con informazioni su profilo, competenze, esperienza e obiettivi professionali di Alessandro.';
  // backend endpoint for AI agent (use your deployed PHP endpoint)
  private aiEndpoint = 'https://www.bompadre.eu/server/chat.php';
  // Knowledge base path (can be overridden in subclasses)
  protected kbPath = 'assets/alessandro-kb.json';

  // in-browser model (always enabled)
  public useInBrowser = true;
  public inBrowserLoading = true;
  public avatarLoading = true;
  public inBrowserError: string | null = null;
  public avatarError: string | null = null;
  public isAwaitingResponse = false;

  constructor(
    private translate: TranslateService,
    private inBrowserModel: InBrowserModelService
  ) {}

  public get quickSuggestions(): string[] {
    return [
      this.translate.t('home.suggestion.profile'),
      this.translate.t('home.suggestion.technical'),
      this.translate.t('home.suggestion.experience'),
      this.translate.t('home.suggestion.leadership'),
      this.translate.t('home.suggestion.contacts')
    ];
  }

  public get isStartupLoading(): boolean {
    return this.inBrowserLoading || this.avatarLoading;
  }

  public get isAssistantReady(): boolean {
    return !this.isStartupLoading;
  }

  public get startupStatusText(): string {
    const loadingAi = this.inBrowserLoading;
    const loadingAvatar = this.avatarLoading;
    if (loadingAi && loadingAvatar) return 'Sto preparando assistente e avatar...';
    if (loadingAi) return 'Sto preparando il modello di risposta...';
    if (loadingAvatar) return 'Sto caricando il personaggio 3D...';
    return 'Pronto';
  }

  // Option 3 light: local static knowledge base loaded from assets.
  private knowledgeBase: KnowledgeEntry[] = [];
  private kbLoaded = false;

  // getter for latest bot message (or null)
  public get lastBotMessage(): string | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].from === 'bot' && (this.messages[i].text || '').trim()) return this.messages[i].text;
    }
    return null;
  }

  // progressive reveal state
  private revealTimer: any = null;
  private revealWords: string[] = [];
  private revealIndex = 0;
  private revealMessageIndex: number | null = null;

  private cancelReveal(): void {
    try { if (this.revealTimer) { clearTimeout(this.revealTimer); this.revealTimer = null; } } catch (e) {}
    this.revealWords = [];
    this.revealIndex = 0;
    this.revealMessageIndex = null;
  }

  private async flushUiForAnimationTransition(): Promise<void> {
    // Force at least one visual frame before heavy model generation starts.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => setTimeout(resolve, 16));
  }

  // Speech APIs
  private recognition: any = null;
  public isListening = false;
  public supportsSpeechRecognition = !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  public supportsSpeechSynthesis = !!(window && (window as any).speechSynthesis);
  public listeningHint: string | null = null;
  // microphone stream + meter
  private micStream?: MediaStream | null = null;
  private audioContext?: AudioContext | null = null;
  private analyser?: AnalyserNode | null = null;
  private meterInterval: any = null;
  public micLevel = 0; // 0..1
  // mic test state
  public testRecording = false;
  public testStatus: string | null = null;
  public testAudioUrl: string | null = null;

  // Start/stop browser speech recognition (STT). Final result will be sent as user message.
  public currentListeningTarget: 'main' | 'message' | 'side' | null = null;

  public toggleListening(target?: 'main' | 'message' | 'side'): void {
    if (!this.supportsSpeechRecognition) return;
    if (this.isListening) this.stopListening(); else this.startListening(target || 'main');
  }

  private async startListening(target: 'main' | 'message' | 'side' = 'main'): Promise<void> {
    try {
      console.log('startListening()', target, 'supportsSpeechRecognition=', this.supportsSpeechRecognition);
      // ensure microphone permission / device is available first and wait for it
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.warn('getUserMedia not available');
      } else {
        try {
          this.listeningHint = 'Richiedo permesso microfono...';
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          try { stream.getTracks().forEach(t => t.stop()); } catch (e) {}
          this.listeningHint = null;
        } catch (err) {
          console.warn('getUserMedia failed', err);
          this.listeningHint = 'Permesso microfono negato';
          return;
        }
      }

      const RecClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!RecClass) { this.supportsSpeechRecognition = false; return; }
      this.recognition = new RecClass();
      // keep listening across multiple utterances where possible
      try { this.recognition.continuous = true; } catch (e) {}
      this.recognition.lang = 'it-IT';
      this.recognition.interimResults = true;
      this.recognition.maxAlternatives = 1;
      let interim = '';
      this.currentListeningTarget = target;
      // start audio meter from getUserMedia stream to show activity
      (async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          this.micStream = stream;
          try {
            const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
            const audioCtx = new AudioCtx();
            this.audioContext = audioCtx;
            const src = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            this.analyser = analyser;
            analyser.fftSize = 256;
            src.connect(analyser);
            const data = new Uint8Array(analyser.fftSize);
            this.meterInterval = setInterval(() => {
              try {
                if (!this.analyser) return;
                this.analyser.getByteTimeDomainData(data);
                let sum = 0;
                for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
                const rms = Math.sqrt(sum / data.length);
                this.micLevel = Math.min(1, rms * 3);
              } catch (e) {}
            }, 80);
          } catch (e) { console.warn('audio meter setup failed', e); }
        } catch (e) {
          console.warn('getUserMedia for meter failed', e);
        }
      })();
      this.recognition.onresult = (ev: any) => {
        console.log('recognition.onresult', ev);
        try {
          interim = '';
          let finalTranscript = '';
          for (let i = ev.resultIndex; i < ev.results.length; i++) {
            const res = ev.results[i];
            const txt = (res[0] && res[0].transcript) ? res[0].transcript : '';
            if (res.isFinal) finalTranscript += txt.trim() + ' ';
            else interim += txt.trim() + ' ';
          }
          // populate the active input with interim/final transcript
          try {
            const setInput = (val: string) => {
              if (this.currentListeningTarget === 'main' && this.mainInputRef && this.mainInputRef.nativeElement) this.mainInputRef.nativeElement.value = val;
              if (this.currentListeningTarget === 'message' && this.messageInputRef && this.messageInputRef.nativeElement) this.messageInputRef.nativeElement.value = val;
              if (this.currentListeningTarget === 'side' && this.sideInputRef && this.sideInputRef.nativeElement) this.sideInputRef.nativeElement.value = val;
            };
            if (interim && interim.trim()) setInput(interim.trim());
            if (finalTranscript && finalTranscript.trim()) {
              // final result: send and clear input
              setInput(finalTranscript.trim());
              this.stopListening();
              this.onSend(finalTranscript.trim());
              // clear the input after a short delay to allow any UI bindings
              setTimeout(() => setInput(''), 200);
            }
          } catch (e) { /* ignore */ }
        } catch (e) { console.warn('recognition onresult failed', e); }
      };
      this.recognition.onstart = () => { console.log('recognition.onstart'); this.isListening = true; this.listeningHint = 'Parla ora...'; };
      this.recognition.onend = () => { console.log('recognition.onend'); this.isListening = false; this.currentListeningTarget = null; this.listeningHint = null; this.cleanupMicMeter(); };
      this.recognition.onaudiostart = () => console.log('recognition.onaudiostart');
      this.recognition.onaudioend = () => console.log('recognition.onaudioend');
      this.recognition.onspeechstart = () => console.log('recognition.onspeechstart');
      this.recognition.onspeechend = () => console.log('recognition.onspeechend');
      this.recognition.onnomatch = (e: any) => console.log('recognition.onnomatch', e);
      this.recognition.onerror = (e: any) => {
        console.warn('recognition error', e);
        // no-speech means the recognizer didn't detect input; show hint and retry briefly
        if (e && e.error === 'no-speech') {
          this.listeningHint = 'Non ti ho sentito, riprova.';
          // try to restart after short delay to give user time
          setTimeout(() => {
            try {
              if (this.isListening && this.recognition) {
                this.recognition.start();
                this.listeningHint = 'Parla ora...';
              }
            } catch (err) { console.warn('restart recognition failed', err); }
          }, 600);
        } else {
          this.listeningHint = 'Errore microfono';
        }
        this.isListening = false;
        this.currentListeningTarget = null;
        this.cleanupMicMeter();
      };
      this.recognition.start();
    } catch (e) { console.warn('startListening failed', e); this.isListening = false; }
  }

  private stopListening(): void {
    try { if (this.recognition) { this.recognition.onresult = null; this.recognition.onend = null; this.recognition.onerror = null; try { this.recognition.stop(); } catch(_){} this.recognition = null; } } catch (e) {}
    this.isListening = false;
    this.currentListeningTarget = null;
    this.cleanupMicMeter();
  }

  // Quick mic test: record ~3s and play back to verify input
  public async recordMicTest(durationMs = 3000): Promise<void> {
    if (this.testRecording) return;
    this.testStatus = 'Richiedo permesso microfono...';
    this.testRecording = true;
    this.testAudioUrl = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.testStatus = 'Registrazione in corso...';
      const recorder = new (window as any).MediaRecorder(stream);
      const chunks: any[] = [];
      recorder.ondataavailable = (ev: any) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
      recorder.onstop = () => {
        try {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          this.testAudioUrl = URL.createObjectURL(blob);
          this.testStatus = 'Riproduco registrazione...';
          const audio = new Audio(this.testAudioUrl);
          audio.onended = () => { this.testStatus = 'Fine test'; this.testRecording = false; try { stream.getTracks().forEach(t => t.stop()); } catch(e){} };
          audio.play().catch((e) => { console.warn('playback failed', e); this.testStatus = 'Playback fallito'; this.testRecording = false; });
        } catch (e) { console.warn('test onstop failed', e); this.testStatus = 'Errore test'; this.testRecording = false; }
      };
      recorder.start();
      setTimeout(() => {
        try { recorder.stop(); } catch (e) { console.warn('stop recorder failed', e); }
      }, durationMs);
    } catch (e) {
      console.warn('recordMicTest failed', e);
      this.testStatus = 'Permesso negato o microfono non disponibile';
      this.testRecording = false;
    }
  }

  // Text-to-speech (TTS) via browser speechSynthesis. Also triggers avatar mouth via `speak()`.
  // Accepts optional onEnd callback invoked when audio playback finishes.
  public speakText(text: string, onEnd?: () => void): void {
    if (!text) {
      if (onEnd) onEnd();
      return;
    }

    const est = Math.max(0.6, Math.min(8, text.length * 0.045));
    const sessionId = ++this.ttsSessionId;
    let ended = false;
    const finish = () => {
      if (ended) return;
      ended = true;
      try { this.stopSpeakingAnimation(); } catch (e) {}
      try { if (onEnd) onEnd(); } catch (e) {}
    };

    try {
      if (!this.supportsSpeechSynthesis) {
        // fallback: still animate mouth and call onEnd after est
        this.speak(est);
        setTimeout(() => {
          if (sessionId !== this.ttsSessionId) return;
          finish();
        }, Math.round(est * 1000));
        return;
      }

      // cancel any ongoing TTS before speaking a new utterance
      try { (window as any).speechSynthesis.cancel(); } catch (e) {}

      const u = new (window as any).SpeechSynthesisUtterance(text);
      const targetLang = this.detectResponseLanguage(text) === 'en' ? 'en-US' : 'it-IT';
      u.lang = targetLang;
      try {
        const selectedVoice = this.selectSpeechVoice(targetLang);
        if (selectedVoice) {
          u.voice = selectedVoice;
          u.lang = selectedVoice.lang || targetLang;
        }
      } catch (e) {
        // keep default voice if selection fails
      }
      // Slightly slower rate for better Italian intelligibility.
      u.rate = targetLang.startsWith('it') ? 0.96 : 1.0;
      u.pitch = 1.0;
      u.onstart = () => {
        if (sessionId !== this.ttsSessionId) return;
        try { this.speak(est); } catch (e) {}
      };
      u.onend = () => {
        if (sessionId !== this.ttsSessionId) return;
        finish();
      };
      u.onerror = (ev: any) => {
        if (sessionId !== this.ttsSessionId) return;
        console.warn('TTS error', ev);
        finish();
      };

      (window as any).speechSynthesis.speak(u);
    } catch (e) {
      console.warn('speakText failed', e);
      this.speak(est);
      setTimeout(() => {
        if (sessionId !== this.ttsSessionId) return;
        finish();
      }, Math.round(est * 1000));
    }
  }

  private selectSpeechVoice(targetLang: string): SpeechSynthesisVoice | null {
    try {
      const synth = (window as any).speechSynthesis;
      if (!synth || typeof synth.getVoices !== 'function') return null;

      const voices: SpeechSynthesisVoice[] = synth.getVoices() || [];
      if (!voices.length) return null;

      const targetLc = String(targetLang || '').toLowerCase();
      const targetBase = targetLc.split('-')[0] || targetLc;

      const exact = voices.find((v) => String(v.lang || '').toLowerCase() === targetLc);
      if (exact) return exact;

      const sameBase = voices.find((v) => String(v.lang || '').toLowerCase().startsWith(targetBase + '-'));
      if (sameBase) return sameBase;

      // If no exact locale, prefer any Italian voice before browser default.
      const italian = voices.find((v) => String(v.lang || '').toLowerCase().startsWith('it'));
      if (targetBase === 'it' && italian) return italian;

      const fallbackDefault = voices.find((v) => !!v.default);
      return fallbackDefault || voices[0] || null;
    } catch (e) {
      return null;
    }
  }

  ngOnInit(): void {
    this.initThree();
    // load model async but don't block
    this.loadModel()
      .catch((e) => {
        console.warn('loadModel failed', e);
        const errMsg = (e && (e as any).message) ? (e as any).message : String(e);
        this.avatarError = errMsg;
      })
      .finally(() => {
        this.avatarLoading = false;
      });
    this.loadKnowledgeBase().catch((e) => console.warn('loadKnowledgeBase failed', e));

    // In-browser model is initialized globally in AppComponent.
    // Check if it's already ready, otherwise wait a moment and check again.
    this.checkInBrowserModelStatus();
  }

  private checkInBrowserModelStatus(): void {
    if (this.inBrowserModel.isReady()) {
      console.log('[HomeComponent] In-browser model already ready (loaded in AppComponent)');
      this.inBrowserLoading = false;
    } else {
      console.log('[HomeComponent] Waiting for in-browser model to be ready...');
      // Poll until model is ready or timeout after 120s
      let attempts = 0;
      const maxAttempts = 120; // 120 * 500ms = 60s
      const checkInterval = setInterval(() => {
        attempts++;
        if (this.inBrowserModel.isReady()) {
          console.log('[HomeComponent] In-browser model ready after polling');
          this.inBrowserLoading = false;
          clearInterval(checkInterval);
        } else if (attempts >= maxAttempts) {
          console.warn('[HomeComponent] In-browser model not ready after 60s (continuing anyway)');
          if (this.inBrowserModel.lastError) {
            this.inBrowserError = typeof this.inBrowserModel.lastError === 'string'
              ? this.inBrowserModel.lastError
              : (this.inBrowserModel.lastError?.message || 'Unknown error');
          }
          this.inBrowserLoading = false;
          clearInterval(checkInterval);
        }
      }, 500);
    }
  }

  ngOnDestroy(): void {
    if (this.animId) cancelAnimationFrame(this.animId);
    try { this.renderer.dispose(); } catch(e) {}
    try { if (this.resizeObserver) this.resizeObserver.disconnect(); } catch(e) {}
  }

  private cleanupMicMeter(): void {
    try {
      if (this.meterInterval) { clearInterval(this.meterInterval); this.meterInterval = null; }
      if (this.analyser) { try { (this.analyser as any).disconnect(); } catch(e){} this.analyser = null; }
      if (this.audioContext) { try { this.audioContext.close(); } catch(e){} this.audioContext = null; }
      if (this.micStream) { try { this.micStream.getTracks().forEach(t => t.stop()); } catch(e){} this.micStream = null; }
      this.micLevel = 0;
    } catch (e) { console.warn('cleanupMicMeter failed', e); }
  }

  private initThree(): void {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 1000);
    this.camera.position.set(0, 1.6, 2.5);

    const hemi = new THREE.HemisphereLight(0xffffff, 0x5f6873, 1.15);
    this.scene.add(hemi);

    // Add a balanced portrait setup so the avatar reads brighter against light backgrounds.
    const key = new THREE.DirectionalLight(0xffffff, 1.35);
    key.position.set(2.2, 2.8, 2.5);
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0xe7f1ff, 0.72);
    fill.position.set(-2.0, 1.8, 2.0);
    this.scene.add(fill);

    const rim = new THREE.DirectionalLight(0xdbe7ff, 0.55);
    rim.position.set(0.4, 2.1, -2.4);
    this.scene.add(rim);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.18;
    // append when container available and ensure canvas fills container
    try {
      const el = this.modelContainer.nativeElement;
      this.renderer.domElement.style.width = '100%';
      this.renderer.domElement.style.height = '100%';
      this.renderer.domElement.style.display = 'block';
      this.renderer.domElement.style.position = 'absolute';
      this.renderer.domElement.style.top = '0';
      this.renderer.domElement.style.left = '0';
      // start hidden for pop-in + fade
      this.renderer.domElement.style.opacity = '0';
      if (!el.style.position) el.style.position = 'relative';
      el.appendChild(this.renderer.domElement);

      // initial resize in next frame to ensure layout is ready
      requestAnimationFrame(() => this.resizeRenderer());

      // use ResizeObserver for reliable resizes when available
      if ((window as any).ResizeObserver) {
        this.resizeObserver = new (window as any).ResizeObserver(() => this.resizeRenderer());
        this.resizeObserver.observe(el);
      } else {
        window.addEventListener('resize', () => this.resizeRenderer());
      }
    } catch (e) {
      console.warn('append renderer failed', e);
    }

    this.animate();
  }

  private resizeRenderer(): void {
    try {
      const el = this.modelContainer.nativeElement;
      const rect = el.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      this.renderer.setSize(w, h, false);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    } catch (e) {
      console.warn('resize failed', e);
    }
  }

  private async loadModel(): Promise<void> {
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader');
    const loader = new GLTFLoader();

    // try load per-model config (position/rotation/scale and camera overrides)
    let cfg: any = null;
    try {
      const resp = await fetch('assets/personaggi/default.config.json');
      if (resp.ok) cfg = await resp.json();
    } catch (e) { /* ignore */ }

    // Support both a single animated GLB and the older per-role GLB setup.
    const configuredSources = cfg?.animation?.roleSources || {};
    const configuredSingleSource = typeof cfg?.animation?.source === 'string'
      ? String(cfg.animation.source)
      : '';
    const configuredRoleNames = cfg?.animation?.roleNames || {};
    const clipSources: Array<{ role: 'idle' | 'talk' | 'think'; url: string }> = [
      { role: 'idle', url: String(configuredSources.idle || 'assets/personaggi/glb/assistente_idle.glb') },
      { role: 'talk', url: String(configuredSources.talk || 'assets/personaggi/glb/assistente_talk.glb') },
      { role: 'think', url: String(configuredSources.think || 'assets/personaggi/glb/assistente_emot.glb') }
    ];

    const describeClip = (c?: THREE.AnimationClip): string => {
      if (!c) return '<missing>';
      return `${c.name} (${c.duration?.toFixed(2)}s, tracks=${c.tracks?.length || 0}, uuid=${c.uuid})`;
    };

    return new Promise(async (resolve, reject) => {
      try {
        const pickClipByConfiguredName = (
          clips: THREE.AnimationClip[],
          role: 'idle' | 'talk' | 'think'
        ): THREE.AnimationClip | undefined => {
          const configuredName = String(configuredRoleNames?.[role] || '').trim().toLowerCase();
          if (!configuredName) return undefined;
          return clips.find((clip) => (clip.name || '').trim().toLowerCase() === configuredName);
        };

        let clipResults: Array<{ role: 'idle' | 'talk' | 'think'; clips: THREE.AnimationClip[] }> = [];
        let allClips: THREE.AnimationClip[] = [];
        let baseModelUrl = configuredSingleSource || clipSources.find((s) => s.role === 'idle')?.url || 'assets/personaggi/glb/assistente_idle.glb';

        if (configuredSingleSource) {
          const singleGltf = await new Promise<any>((res, rej) => {
            loader.load(configuredSingleSource, (gltf: any) => res(gltf), undefined, rej);
          });
          allClips = Array.isArray(singleGltf?.animations) ? singleGltf.animations : [];
          clipResults = clipSources.map((src) => ({ role: src.role, clips: allClips }));
          this.roleClips = {
            idle: pickClipByConfiguredName(allClips, 'idle'),
            talk: pickClipByConfiguredName(allClips, 'talk'),
            think: pickClipByConfiguredName(allClips, 'think')
          };
          console.log(`[Loading ALL roles from ${configuredSingleSource.split('/').pop() || configuredSingleSource}] ->`,
            allClips.map((c) => describeClip(c))
          );
        } else {
          const clipPromises = clipSources.map((src) =>
            new Promise<{ role: 'idle' | 'talk' | 'think'; clips: THREE.AnimationClip[] }>((res) => {
              loader.load(src.url, (gltf: any) => {
                const clips = Array.isArray(gltf.animations) ? gltf.animations : [];
                res({ role: src.role, clips });
              }, undefined, () => res({ role: src.role, clips: [] }));
            })
          );

          clipResults = await Promise.all(clipPromises);
          allClips = clipResults.flatMap((r) => r.clips);

          clipResults.forEach((result) => {
            const fileName = clipSources.find((s) => s.role === result.role)?.url.split('/').pop() || 'unknown';
            const clipInfo = result.clips.map((c) => describeClip(c)).join(', ') || '<no clips>';
            console.log(`[Loading ${result.role.toUpperCase()} from ${fileName}] -> ${clipInfo}`);
          });

          this.roleClips = {
            idle: pickClipByConfiguredName(clipResults.find((r) => r.role === 'idle')?.clips || [], 'idle') || clipResults.find((r) => r.role === 'idle')?.clips?.[0],
            talk: pickClipByConfiguredName(clipResults.find((r) => r.role === 'talk')?.clips || [], 'talk') || clipResults.find((r) => r.role === 'talk')?.clips?.[0],
            think: pickClipByConfiguredName(clipResults.find((r) => r.role === 'think')?.clips || [], 'think') || clipResults.find((r) => r.role === 'think')?.clips?.[0]
          };
          console.log(`Loaded ${allClips.length} animation clips from separate files:`,
            allClips.map((c) => describeClip(c))
          );
          console.log('Role source mapping:', {
            idle: clipSources.find((s) => s.role === 'idle')?.url,
            talk: clipSources.find((s) => s.role === 'talk')?.url,
            think: clipSources.find((s) => s.role === 'think')?.url
          });
        }

        console.log('Role clip mapping:', {
          idle: describeClip(this.roleClips.idle),
          talk: describeClip(this.roleClips.talk),
          think: describeClip(this.roleClips.think)
        });

        loader.load(baseModelUrl, async (gltf: any) => {
          try {
            const model = gltf.scene || gltf.scenes[0];
            if (!model) return resolve();
            model.name = 'avatarModel';
            const materialDiagRows: Array<Record<string, any>> = [];
            model.traverse((c: any) => {
              if (c.isMesh) {
                c.castShadow = true;
                c.receiveShadow = true;
                const name = (c.name || '').toLowerCase();
                const isEye = /eye|eyeball|eyelid|pupil|sclera|iris|occhio|palpebra|lid/.test(name);
                if (isEye) {
                  try { this.eyeMeshes.push({ mesh: c as THREE.Mesh, initialQuat: (c as THREE.Mesh).quaternion.clone() }); } catch(e) {}
                }
                if (Array.isArray(c.morphTargetInfluences) && c.morphTargetInfluences.length > 0) {
                  // only treat as mouth morph if not an eye mesh
                  if (!isEye) this.mouthMorphMeshes.push(c as THREE.Mesh);
                }

                // Apply targeted material policy to avoid alpha sorting artifacts on CC avatars.
                const mats: any[] = Array.isArray(c.material) ? c.material : (c.material ? [c.material] : []);
                for (const mat of mats) {
                  this.fixAvatarMaterial(mat, name, materialDiagRows);
                }
              }
            });
            if (!this.materialDiagnosticsPrinted && materialDiagRows.length > 0) {
              this.materialDiagnosticsPrinted = true;
              try {
                console.groupCollapsed('[Avatar materials] effective policy');
                console.table(materialDiagRows);
                const focusRows = materialDiagRows.filter((r) =>
                  /ciglia|lash|eyelash|barba|beard|baffi|mustache|camicia|shirt/i.test(`${r['mesh']} ${r['material']}`)
                );
                if (focusRows.length > 0) {
                  console.groupCollapsed('[Avatar materials] focus beard/lashes/shirt');
                  console.table(focusRows);
                  console.groupEnd();
                }
                console.groupEnd();
              } catch (e) {
                console.log('[Avatar materials] effective policy', materialDiagRows);
              }
            }
            this.scene.add(model);
            // keep reference for pop-in animation
            this.avatarModel = model;

            // center
            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            model.position.sub(center);
            model.updateMatrixWorld(true);

            // scale to reasonable height
            const size = box.getSize(new THREE.Vector3());
            const desired = 1.4;
            if (size.y > 0.0001) {
              const s = desired / size.y;
              model.scale.setScalar((model.scale.x || 1) * s);
              model.updateMatrixWorld(true);
            }

            // align bottom to y=0
            const box2 = new THREE.Box3().setFromObject(model);
            const min2 = box2.min.clone();
            model.position.y -= min2.y;
            model.updateMatrixWorld(true);

            // apply config model transform (position/rotation/scale) if provided
            if (cfg && cfg.model) {
              if (typeof cfg.model.scale === 'number') {
                model.scale.setScalar((model.scale.x || 1) * cfg.model.scale);
                model.updateMatrixWorld(true);
              }
              if (cfg.model.rotation) {
                const units = (cfg.model.rotation.units || 'deg').toLowerCase();
                const toRad = (v: number) => units === 'deg' ? v * Math.PI / 180 : v;
                const rx = toRad(cfg.model.rotation.x || 0);
                const ry = toRad(cfg.model.rotation.y || 0);
                const rz = toRad(cfg.model.rotation.z || 0);
                model.rotation.set(rx, ry, rz);
                model.updateMatrixWorld(true);
              }
              if (cfg.model.position) {
                model.position.set(
                  typeof cfg.model.position.x === 'number' ? cfg.model.position.x : model.position.x,
                  typeof cfg.model.position.y === 'number' ? cfg.model.position.y : model.position.y,
                  typeof cfg.model.position.z === 'number' ? cfg.model.position.z : model.position.z
                );
                model.updateMatrixWorld(true);
              }
            }

            this.avatarBaseRotation = model.rotation.clone();

            // setup pop-in: capture final scale, then start from tiny scale
            try {
              this.popInToScale = model.scale.clone();
              this.popInFromScale = new THREE.Vector3(0.001, 0.001, 0.001);
              model.scale.copy(this.popInFromScale);
              model.updateMatrixWorld(true);
              this.popInStart = performance.now();
              // allow override from config: cfg.animation.popInDuration (ms)
              if (cfg && cfg.animation && typeof cfg.animation.popInDuration === 'number') this.popInDuration = cfg.animation.popInDuration;
              this.popInActive = true;
              // setup fade as well (canvas opacity)
              this.fadeStart = performance.now();
              if (cfg && cfg.animation && typeof cfg.animation.fadeDuration === 'number') this.fadeDuration = cfg.animation.fadeDuration;
              this.fadeActive = true;
              try { if (this.renderer && this.renderer.domElement) this.renderer.domElement.style.opacity = String(this.fadeFrom); } catch(e) {}
            } catch (e) {
              // ignore
            }

            // camera framing (unless config overrides camera)
            if (cfg && cfg.camera && (cfg.camera.position || cfg.camera.lookAt)) {
              if (cfg.camera.position) {
                this.camera.position.set(cfg.camera.position.x || this.camera.position.x,
                  cfg.camera.position.y || this.camera.position.y,
                  cfg.camera.position.z || this.camera.position.z);
              }
              if (cfg.camera.lookAt) {
                this.camera.lookAt(new THREE.Vector3(cfg.camera.lookAt.x || 0, cfg.camera.lookAt.y || 0, cfg.camera.lookAt.z || 0));
              }
              this.camera.updateProjectionMatrix();
            } else {
              const finalBox = new THREE.Box3().setFromObject(model);
              const finalCenter = finalBox.getCenter(new THREE.Vector3());
              const finalSize = finalBox.getSize(new THREE.Vector3());
              const dist = Math.max(1.6, finalSize.y * 2.2);
              this.camera.position.set(finalCenter.x, finalCenter.y + dist * 0.5, finalCenter.z + dist);
              this.camera.lookAt(finalCenter);
              this.camera.updateProjectionMatrix();
            }

            // Use all collected clips from separate files
            this.availableClips = allClips;
            // read speak duration from config if present
            try { if (cfg && cfg.animation && typeof cfg.animation.speakDuration === 'number') this.defaultSpeakDuration = cfg.animation.speakDuration; } catch(e) {}
            if (this.availableClips && this.availableClips.length > 0) {
              this.mixer = new THREE.AnimationMixer(model);
              console.log(`Animations loaded (${this.availableClips.length} total):`,
                this.availableClips.map((c: any, i: number) => `[${i}] ${describeClip(c)}`)
              );
              console.log('Raw animation objects:', this.availableClips);
              this.resolveAnimationClips();
              try {
                this.playIdleAnimation(true);
                console.log('Autoplay idle clip:', (this.idleClip && this.idleClip.name) || '<clip>');
              } catch (e) {
                console.warn('Autoplay idle clip failed', e);
              }
            }

            console.log('GLTF model loaded with separate animation clips');
            resolve();
          } catch (e) { reject(e); }
        }, undefined, (err: any) => reject(err));
      } catch (e) {
        reject(e);
      }
    });
  }

  private animate = (): void => {
    this.animId = requestAnimationFrame(this.animate);
    const now = performance.now();
    const delta = (now - this.lastTime) / 1000;
    this.lastTime = now;
    try {
      if (this.mixer) this.mixer.update(delta);
      if (this.avatarModel && this.avatarBaseRotation) {
        const r = this.avatarModel.rotation;
        if (this.avatarMotionState === 'think' && this.isAwaitingResponse) {
          const t = Math.max(0, (now - this.thinkMotionStart) / 1000);
          const targetX = this.avatarBaseRotation.x + Math.sin(t * 3.0) * 0.02;
          const targetY = this.avatarBaseRotation.y + Math.sin(t * 1.8) * 0.08;
          r.x = THREE.MathUtils.lerp(r.x, targetX, 0.12);
          r.y = THREE.MathUtils.lerp(r.y, targetY, 0.12);
        } else {
          r.x = THREE.MathUtils.lerp(r.x, this.avatarBaseRotation.x, 0.1);
          r.y = THREE.MathUtils.lerp(r.y, this.avatarBaseRotation.y, 0.1);
        }
      }

      // pop-in animation: interpolate model scale from tiny -> final
      if (this.popInActive && this.avatarModel) {
        const elapsed = now - this.popInStart;
        const t = Math.min(1, Math.max(0, elapsed / this.popInDuration));
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        try {
          this.avatarModel.scale.set(
            THREE.MathUtils.lerp(this.popInFromScale.x, this.popInToScale.x, eased),
            THREE.MathUtils.lerp(this.popInFromScale.y, this.popInToScale.y, eased),
            THREE.MathUtils.lerp(this.popInFromScale.z, this.popInToScale.z, eased)
          );
          this.avatarModel.updateMatrixWorld(true);
        } catch (e) {}
        if (t >= 1) this.popInActive = false;
      }

      // fade animation: interpolate canvas opacity
      if (this.fadeActive) {
        try {
          const elapsedF = now - this.fadeStart;
          const tf = Math.min(1, Math.max(0, elapsedF / this.fadeDuration));
          // easeOutCubic
          const easedF = 1 - Math.pow(1 - tf, 3);
          const cur = THREE.MathUtils.lerp(this.fadeFrom, this.fadeTo, easedF);
          if (this.renderer && this.renderer.domElement) this.renderer.domElement.style.opacity = String(cur);
          if (tf >= 1) {
            this.fadeActive = false;
            if (this.renderer && this.renderer.domElement) this.renderer.domElement.style.opacity = String(this.fadeTo);
          }
        } catch (e) {}
      }

      // morph-based speak fallback (used only when no talk clip is available)
      if (this.speaking && this.useMorphSpeaking && this.mouthMorphMeshes.length > 0) {
        const t = (now - this.speakStart) / 1000; // seconds since speak started
        const total = Math.max(0.001, (this.speakEnd - this.speakStart) / 1000);
        const env = Math.max(0, 1 - Math.pow((t / total) - 0.5, 2) * 4); // simple envelope
        const osc = Math.abs(Math.sin(t * 20));
        const weight = env * osc * 0.8;
        for (const m of this.mouthMorphMeshes) {
          const len = m.morphTargetInfluences ? m.morphTargetInfluences.length : 0;
          if (len > 0) {
            for (let i = 0; i < Math.min(3, len); i++) {
              m.morphTargetInfluences![i] = Math.max(0, Math.min(1, weight * (1 - i * 0.33)));
            }
          }
        }
        if (now > this.speakEnd) {
          this.stopSpeakingAnimation();
        }
      }

      // ensure eyes keep their original orientation to avoid jitter caused by morph fallback
      for (const e of this.eyeMeshes) {
        try { e.mesh.quaternion.slerp(e.initialQuat, 0.2); } catch (e_) {}
      }

      this.renderer.render(this.scene, this.camera);
    } catch (e) {}
  }

  private fixAvatarMaterial(mat: any, meshNameLc: string, materialDiagRows: Array<Record<string, any>>): void {
    if (!mat) return;

    const matNameLc = String(mat.name || '').toLowerCase();
    const fullName = `${meshNameLc} ${matNameLc}`;
    const isStdEyelash = /(^|\s|_)std[_\-\s]?eyelash($|\s|_)/.test(matNameLc);
    const isUpperLowerEyelashOverlay = /eyelash[_\-\s]?(up|low)[_\-\s]?transparency/.test(matNameLc);
    const hasOpacityMap = !!(mat.alphaMap || mat.opacityMap);
    const hasColorMap = !!(mat.map && (mat.map as any).image);
    const hasAlphaHint = /alpha|opacity|transparency|eyelash|lash|ciglia|brow|eyebrow|sopracciglio|beard|barba|mustache|moustache|baffi|stubble|tearline/.test(fullName);
    const isGenericEyeSurface = /\beye\b/.test(fullName) && !/eyelash|lash|ciglia/.test(fullName);
    const isEyeBall = /eyeball|eye_ball|sclera|iris|pupil|occhio/.test(fullName) || isGenericEyeSurface;
    const isEyeWetLayer = /cornea|tearline|waterline/.test(fullName);
    const isEyeOcclusion = /eye[_\-\s]?occlusion/.test(fullName);
    const isLashOrBrow = /eyelash|lash|ciglia|brow|eyebrow|sopracciglio/.test(fullName);
    const isEyelashOverlay = isUpperLowerEyelashOverlay || /stubble_[56]/.test(fullName);
    const isFacialHair = /beard|barba|mustache|moustache|baffi|stubble|goatee/.test(fullName);
    const isSkinLike = /skin|face|head|body|neck|ear|scalp|forehead|cheek|nose|lip/.test(fullName)
      && !isEyeBall
      && !isEyeWetLayer
      && !isEyeOcclusion
      && !isLashOrBrow
      && !isFacialHair;
    const isClothLike = /shirt|tshirt|cloth|jacket|coat|sweater|hoodie|top|camicia/.test(fullName);
    const isExplicitAlphaCloth = /lace|net|mesh|transparent|sheer/.test(fullName);
    const usesCardAlpha = hasOpacityMap || hasAlphaHint || isLashOrBrow || isFacialHair;

    // Preserve texture color profile to avoid desaturated/white-like appearance.
    if (mat.map) {
      try {
        mat.map.colorSpace = THREE.SRGBColorSpace;
        mat.map.needsUpdate = true;
      } catch (e) {}
    }

    // Eye ball (iris/sclera/pupil) must stay opaque, otherwise it can disappear.
    if (isEyeBall) {
      mat.transparent = false;
      mat.depthWrite = true;
      mat.alphaTest = 0;
      mat.side = THREE.FrontSide;
      if ('metalness' in mat) mat.metalness = Math.min(Number(mat.metalness ?? 0), 0.02);
      if ('roughness' in mat) mat.roughness = Math.max(Number(mat.roughness ?? 1), 0.65);
      mat.needsUpdate = true;
    }

    // Wet eye layers are translucent but should not use alpha-test clipping.
    if (isEyeWetLayer) {
      mat.transparent = true;
      mat.depthWrite = false;
      mat.alphaTest = 0;
      mat.side = THREE.FrontSide;
      if ('opacity' in mat) mat.opacity = Math.max(0.2, Math.min(Number(mat.opacity ?? 1), 0.55));
      if ('metalness' in mat) mat.metalness = Math.max(Number(mat.metalness ?? 0), 0.0);
      if ('roughness' in mat) mat.roughness = Math.min(Number(mat.roughness ?? 1), 0.25);
      mat.needsUpdate = true;
    }

    // Problematic eyelash overlays from CC export can appear as solid dark strips.
    // Keep base eyelash material active and disable only these extra overlay cards.
    if (isEyelashOverlay) {
      if ('visible' in mat) mat.visible = false;
      mat.transparent = true;
      mat.depthWrite = false;
      mat.alphaTest = 0.95;
      if ('opacity' in mat) mat.opacity = 0;
      if ('colorWrite' in mat) mat.colorWrite = false;
      mat.needsUpdate = true;
    }

    // Base eyelash card from CC often needs a much stronger cut to avoid thick painted bands.
    if (isStdEyelash) {
      mat.transparent = true;
      mat.depthWrite = false;
      mat.side = THREE.FrontSide;
      mat.alphaTest = Math.max(0.62, Number(mat.alphaTest || 0));
      if ('opacity' in mat) mat.opacity = Math.min(0.55, Math.max(0.35, Number(mat.opacity ?? 1)));
      if ('premultipliedAlpha' in mat) mat.premultipliedAlpha = true;
      if ('metalness' in mat) mat.metalness = Math.min(Number(mat.metalness ?? 0), 0.02);
      if ('roughness' in mat) mat.roughness = Math.max(Number(mat.roughness ?? 1), 0.9);
      mat.needsUpdate = true;
    }

    // Lashes/brows: stronger cut to avoid thick dark halos.
    if (isLashOrBrow && !isEyeBall && !isEyeWetLayer) {
      mat.transparent = true;
      mat.depthWrite = false;
      mat.side = THREE.FrontSide;
      mat.alphaTest = Math.max(0.2, Number(mat.alphaTest || 0));
      if ('opacity' in mat) mat.opacity = Math.min(0.9, Math.max(0.65, Number(mat.opacity ?? 1)));
      if ('premultipliedAlpha' in mat) mat.premultipliedAlpha = true;
      if ('metalness' in mat) mat.metalness = Math.min(Number(mat.metalness ?? 0), 0.03);
      if ('roughness' in mat) mat.roughness = Math.max(Number(mat.roughness ?? 1), 0.88);
      mat.needsUpdate = true;
    }

    // Facial hair cards: front-side only + higher alpha cut to reduce over-visible beard mass.
    if (isFacialHair && !isEyeBall && !isEyeWetLayer) {
      mat.transparent = true;
      mat.depthWrite = false;
      mat.side = THREE.FrontSide;
      mat.alphaTest = Math.max(0.28, Number(mat.alphaTest || 0));
      if ('opacity' in mat) mat.opacity = Math.min(0.78, Math.max(0.58, Number(mat.opacity ?? 1)));
      if ('premultipliedAlpha' in mat) mat.premultipliedAlpha = true;
      if ('metalness' in mat) mat.metalness = Math.min(Number(mat.metalness ?? 0), 0.04);
      if ('roughness' in mat) mat.roughness = Math.max(Number(mat.roughness ?? 1), 0.86);
      mat.needsUpdate = true;
    }

    // Generic alpha-card materials: keep depth write off and use alpha test to remove fringes.
    if (usesCardAlpha && !isEyeBall && !isEyeWetLayer && !isLashOrBrow && !isFacialHair) {
      mat.transparent = true;
      mat.depthWrite = false;
      mat.side = THREE.FrontSide;
      mat.alphaTest = Math.max(0.16, Number(mat.alphaTest || 0));
      if ('premultipliedAlpha' in mat) mat.premultipliedAlpha = true;
      if ('metalness' in mat) mat.metalness = Math.min(Number(mat.metalness ?? 0), 0.05);
      if ('roughness' in mat) mat.roughness = Math.max(Number(mat.roughness ?? 1), 0.8);
      mat.needsUpdate = true;
    }

    // Clothing should stay opaque unless explicitly transparent (e.g. lace/net).
    if (isClothLike && !isExplicitAlphaCloth) {
      mat.transparent = false;
      mat.depthWrite = true;
      mat.alphaTest = 0;
      mat.side = THREE.FrontSide;
      if ('opacity' in mat) mat.opacity = 1;
      if ('premultipliedAlpha' in mat) mat.premultipliedAlpha = false;
      if ('metalness' in mat) mat.metalness = Math.min(Number(mat.metalness ?? 0), 0.08);
      if ('roughness' in mat) mat.roughness = Math.max(Number(mat.roughness ?? 1), 0.82);
      // If cloth texture is missing and the material is near-white, force a muted green fallback.
      if (!hasColorMap && mat.color && typeof mat.color.setRGB === 'function') {
        try {
          const r = Number(mat.color.r ?? 1);
          const g = Number(mat.color.g ?? 1);
          const b = Number(mat.color.b ?? 1);
          const isNearWhite = r > 0.8 && g > 0.8 && b > 0.8;
          if (isNearWhite) {
            mat.color.setRGB(0.20, 0.46, 0.25);
          }
        } catch (e) {}
      }
      if ('emissive' in mat && mat.emissive && typeof mat.emissive.setRGB === 'function') {
        try { mat.emissive.setRGB(0, 0, 0); } catch (e) {}
      }
      mat.needsUpdate = true;
    }

    // Skin should stay mostly matte to avoid sweaty highlights under portrait lights.
    if (isSkinLike) {
      mat.transparent = false;
      mat.depthWrite = true;
      mat.alphaTest = 0;
      mat.side = THREE.FrontSide;
      if ('opacity' in mat) mat.opacity = 1;
      if ('metalness' in mat) mat.metalness = Math.min(Number(mat.metalness ?? 0), 0.015);
      if ('roughness' in mat) mat.roughness = Math.max(Number(mat.roughness ?? 1), 0.78);
      if ('envMapIntensity' in mat) mat.envMapIntensity = Math.min(Number(mat.envMapIntensity ?? 1), 0.35);
      mat.needsUpdate = true;
    }

    // CC eye occlusion shells can be exported as opaque and completely hide iris/pupil.
    // Disable that layer and keep the actual eyeball/cornea visible.
    if (isEyeOcclusion) {
      mat.transparent = true;
      mat.depthWrite = false;
      mat.alphaTest = 0;
      mat.side = THREE.FrontSide;
      if ('opacity' in mat) mat.opacity = 0;
      if ('colorWrite' in mat) mat.colorWrite = false;
      if ('premultipliedAlpha' in mat) mat.premultipliedAlpha = true;
      if ('metalness' in mat) mat.metalness = 0;
      if ('roughness' in mat) mat.roughness = 1;
      mat.needsUpdate = true;
    }

    // Track what policy was applied for deterministic debugging.
    const appliedPolicy = isEyeBall
      ? 'eye-opaque'
      : isEyeWetLayer
        ? 'eye-wet'
        : isEyeOcclusion
          ? 'eye-occlusion-off'
          : isStdEyelash
            ? 'lash-base-thin'
            : isEyelashOverlay
              ? 'lash-overlay-off'
              : isLashOrBrow
                ? 'lash-brow'
                : isFacialHair
                  ? 'facial-hair'
                  : (usesCardAlpha && !isClothLike)
                    ? 'alpha-card'
                    : (isClothLike && !isExplicitAlphaCloth)
                      ? 'opaque-cloth'
                      : (isSkinLike ? 'skin-matte' : 'default');

    materialDiagRows.push({
      mesh: meshNameLc,
      material: mat.name || '<unnamed>',
      transparent: !!mat.transparent,
      depthWrite: !!mat.depthWrite,
      alphaTest: Number(mat.alphaTest || 0),
      side: mat.side === THREE.DoubleSide ? 'DoubleSide' : (mat.side === THREE.BackSide ? 'BackSide' : 'FrontSide'),
      hasAlphaMap: hasOpacityMap,
      hasColorMap,
      policy: appliedPolicy
    });
  }

  speak(duration?: number): void {
    duration = typeof duration === 'number' ? duration : this.defaultSpeakDuration;
    const now = performance.now();
    if (this.availableClips && this.availableClips.length > 0 && this.mixer) {
      this.useMorphSpeaking = false;
      this.speaking = true;
      this.playTalkAnimation(duration);
      this.speakStart = now;
      this.speakEnd = now + duration * 1000;
      console.log('Playing talk clip', (this.talkClip && this.talkClip.name) || '<clip>');
      return;
    }

    // no clip -> morph fallback
    this.useMorphSpeaking = true;
    this.speaking = true;
    this.speakStart = now;
    this.speakEnd = now + duration * 1000;
    console.log('Using morph fallback for speak (slower)');
  }

  private resolveAnimationClips(): void {
    const clips = this.availableClips || [];
    if (!clips.length) return;

    const describe = (c?: THREE.AnimationClip) => {
      if (!c) return '<missing>';
      return `${c.name} (${c.duration?.toFixed(2)}s, tracks=${c.tracks?.length || 0}, uuid=${c.uuid})`;
    };

    const nameOf = (c: THREE.AnimationClip) => (c.name || '').toLowerCase();
    const isTalkLike = (c: THREE.AnimationClip) => /talk|speak|speech|chat|answer|mouth|phoneme|lipsync/.test(nameOf(c));
    const isIdleLike = (c: THREE.AnimationClip) => /\bidle\b|breath|breathe|stand|neutral|rest/.test(nameOf(c));
    const isThinkLike = (c: THREE.AnimationClip) => /think|thinking|emot|emotion|reflect|ponder/.test(nameOf(c));

    const exactIdle = clips.find((c) => /assistente[_\-\s]?idle/.test(nameOf(c)));
    const exactTalk = clips.find((c) => /assistente[_\-\s]?talk/.test(nameOf(c)));
    const exactThink = clips.find((c) => /assistente[_\-\s]?emot/.test(nameOf(c)));
    const roleIdle = this.roleClips.idle;
    const roleTalk = this.roleClips.talk;
    const roleThink = this.roleClips.think;

    // Idle must never be a talk-like clip.
    this.idleClip =
      roleIdle ||
      exactIdle ||
      clips.find((c) => isIdleLike(c) && !isTalkLike(c)) ||
      clips.find((c) => !isTalkLike(c)) ||
      clips[0];

    // Talk clip should be explicitly talk-like and different from idle.
    // If only one clip exists (single-animation model), reuse it for both states.
    this.talkClip =
      (roleTalk && roleTalk !== this.idleClip ? roleTalk : undefined) ||
      (exactTalk && exactTalk !== this.idleClip ? exactTalk : undefined) ||
      clips.find((c) => c !== this.idleClip && isTalkLike(c)) ||
      clips.find((c) => c !== this.idleClip) ||
      this.idleClip;

    // Thinking clip can be used while the assistant is generating a response.
    this.thinkClip =
      (roleThink && roleThink !== this.idleClip ? roleThink : undefined) ||
      (exactThink && exactThink !== this.idleClip ? exactThink : undefined) ||
      clips.find((c) => c !== this.idleClip && c !== this.talkClip && isThinkLike(c)) ||
      this.idleClip;

    console.log('Selected idle clip:', describe(this.idleClip));
    console.log('Selected talk clip:', describe(this.talkClip));
    console.log('Selected think clip:', describe(this.thinkClip));
  }

  private playIdleAnimation(forceReset = false): void {
    if (!this.mixer || !this.idleClip) return;
    if (!this.idleAction || forceReset) this.idleAction = this.mixer.clipAction(this.idleClip);

    const next = this.idleAction;
    next.enabled = true;
    next.clampWhenFinished = false;
    next.setLoop(THREE.LoopRepeat, Infinity);
    if (forceReset) next.reset();
    next.timeScale = 1;
  next.timeScale = 0.95;

    if (this.activeAction && this.activeAction !== next) {
      next.reset();
      next.play();
      next.crossFadeFrom(this.activeAction, 0.25, true);
    } else if (!next.isRunning()) {
      next.play();
    }

    this.activeAction = next;
    this.avatarMotionState = 'idle';
  }

  private playTalkAnimation(duration: number): void {
    if (!this.mixer || !this.talkClip) return;
    if (!this.talkAction) this.talkAction = this.mixer.clipAction(this.talkClip);

    const next = this.talkAction;
    next.enabled = true;
    next.clampWhenFinished = false;
    next.setLoop(THREE.LoopRepeat, Infinity);
    next.reset();

    try {
      const clipDur = Math.max(0.001, this.talkClip.duration || 1);
      const targetDur = Math.max(0.01, duration);
      let timeScale = clipDur / targetDur;
      if (!isFinite(timeScale) || timeScale <= 0) timeScale = 1;
      timeScale = Math.max(0.6, Math.min(1.6, timeScale));
      next.timeScale = timeScale;
    } catch (e) {
      next.timeScale = 1;
    }

    if (this.activeAction && this.activeAction !== next) {
      next.play();
      next.crossFadeFrom(this.activeAction, 0.2, true);
    } else {
      next.play();
    }

    this.activeAction = next;
    this.avatarMotionState = 'talk';
  }

  private playThinkingAnimation(): void {
    if (!this.mixer || !this.thinkClip) return;
    if (!this.thinkAction) this.thinkAction = this.mixer.clipAction(this.thinkClip);

    const next = this.thinkAction;
    next.enabled = true;
    next.clampWhenFinished = false;
    next.setLoop(THREE.LoopRepeat, Infinity);
    next.timeScale = this.thinkClip === this.idleClip ? 1.28 : 1.08;

    if (this.activeAction && this.activeAction !== next) {
      next.reset();
      next.play();
      next.crossFadeFrom(this.activeAction, 0.2, true);
    } else if (!next.isRunning()) {
      next.reset();
      next.play();
    }

    this.activeAction = next;
    this.thinkMotionStart = performance.now();
    this.avatarMotionState = 'think';
  }

  private stopSpeakingAnimation(): void {
    this.speaking = false;
    this.useMorphSpeaking = false;
    for (const m of this.mouthMorphMeshes) {
      if (m.morphTargetInfluences) {
        for (let i = 0; i < m.morphTargetInfluences.length; i++) {
          m.morphTargetInfluences[i] = 0;
        }
      }
    }
    this.playIdleAnimation();
  }

  // Called from template when user submits text
  async onSend(text?: string): Promise<void> {
    if (this.isAwaitingResponse) return;
    if (this.isStartupLoading) return;
    try {
      const t = (text || '').trim();
      if (!t) return;

      // New question: stop any previous TTS session and keep avatar in idle while waiting.
      this.ttsSessionId++;
      try { if (this.supportsSpeechSynthesis) (window as any).speechSynthesis.cancel(); } catch (e) {}
      this.stopSpeakingAnimation();
      this.playThinkingAnimation();

      this.isAwaitingResponse = true;
      // Ensure thinking pose and spinner become visible before generation starts.
      await this.flushUiForAnimationTransition();
      // push user message
      this.messages.push({ from: 'user', text: t });

      // insert bot placeholder and cancel any ongoing reveal
      this.cancelReveal();
      const idx = this.messages.push({ from: 'bot', text: '' }) - 1;
      this.revealMessageIndex = idx;

      // query AI agent (backend endpoint should return JSON { reply: string } or plain text)
      let reply: string = this.botReply;
      try {
        if (this.useInBrowser) {
          try {
            const extracted = await this.generateInBrowserReply(t);
            reply = extracted || this.buildLocalFallbackReply(t);
          } catch (e) {
            console.warn('in-browser model failed', e);
            reply = this.buildLocalFallbackReply(t);
          }
        } else {
          reply = await this.queryAI(t);
        }
      } catch (e) {
        console.warn('queryAI failed, using fallback reply', e);
        reply = this.buildLocalFallbackReply(t);
      }

      // debug: log raw reply and sanitize repeated segments
      try { console.log('AI reply (raw):', reply); } catch (e) {}
      try { reply = this.sanitizeReply(String(reply)); } catch (e) { console.warn('sanitizeReply failed', e); }
      // estimate TTS duration from reply length
      const est = Math.max(0.6, Math.min(8, reply.length * 0.045));

      // schedule progressive reveal of reply over estimated duration
      this.revealWords = reply.split(/\s+/).filter(Boolean);
      this.revealIndex = 0;
      const totalMs = Math.max(200, Math.round(est * 1000));
      const perWordMs = Math.max(30, Math.round(totalMs / Math.max(1, this.revealWords.length)));

      const step = () => {
        try {
          if (this.revealMessageIndex === null) return;
          if (this.revealIndex >= this.revealWords.length) {
            this.cancelReveal();
            return;
          }
          const cur = this.messages[this.revealMessageIndex].text || '';
          const next = this.revealWords[this.revealIndex];
          this.messages[this.revealMessageIndex].text = (cur ? cur + ' ' : '') + next;
          this.revealIndex++;
          this.revealTimer = setTimeout(step, perWordMs);
        } catch (e) { this.cancelReveal(); }
      };

      this.revealTimer = setTimeout(step, 0);

      // speak reply and ensure reveal completes on end
      this.speakText(reply, () => {
        try {
          if (this.revealMessageIndex !== null) this.messages[this.revealMessageIndex].text = reply;
        } catch (e) {}
        this.cancelReveal();
      });
    } catch (e) { console.warn('onSend failed', e); }
    finally { this.isAwaitingResponse = false; }
  }

  public onSuggestionClick(suggestion: string, inputEl?: HTMLInputElement): void {
    if (this.isAwaitingResponse) return;
    const prompt = (suggestion || '').trim();
    if (!prompt) return;
    if (inputEl) inputEl.value = '';
    this.onSend(prompt);
  }

  // Multi-attempt generation for small in-browser models.
  private async generateInBrowserReply(userText: string): Promise<string> {
    const startTime = Date.now();
    console.log('[GENERATE] Starting with input:', userText);

    // Lightweight intent routing: avoid wasting small-model generation on trivial greetings.
    if (this.isSimpleGreetingOrSmalltalk(userText)) {
      console.log('[GENERATE] Simple greeting/smalltalk detected, using local reply');
      return this.buildLocalFallbackReply(userText);
    }
    
    if (!this.inBrowserModel.isReady()) {
      console.log('[GENERATE] Model not ready, initializing...');
      await this.inBrowserModel.init();
      console.log('[GENERATE] Model initialized');
    }

    const attempts = this.buildGenerationAttempts(userText);
    let bestCandidate = '';
    let bestScore = Number.NEGATIVE_INFINITY;

    for (let attemptIdx = 0; attemptIdx < attempts.length; attemptIdx++) {
      const a = attempts[attemptIdx];
      try {
        console.log(`[GENERATE] Attempt ${attemptIdx + 1}/3: maxTokens=${a.maxNewTokens}`);
        const raw = await this.inBrowserModel.generate(a.prompt, {
          max_new_tokens: a.maxNewTokens,
          do_sample: a.doSample,
          temperature: a.temperature,
          top_p: a.topP,
          repetition_penalty: a.repetitionPenalty,
          no_repeat_ngram_size: 3,
          return_full_text: false
        });
        console.log(`[GENERATE] Raw output (${(String(raw || '')).length} chars):`, String(raw || '').substring(0, 200));
        
        const extracted = this.extractAssistantReply(String(raw || ''), a.prompt);
        console.log(`[GENERATE] After extract (${extracted.length} chars):`, extracted.substring(0, 150));
        
        const cleaned = this.sanitizeReply(extracted).trim();
        console.log(`[GENERATE] After sanitize (${cleaned.length} chars):`, cleaned.substring(0, 150));

        const cleanedScore = this.scoreReplyCandidate(userText, cleaned);
        console.log(`[GENERATE] Candidate score: ${cleanedScore}`);

        if (cleanedScore > bestScore) {
          bestScore = cleanedScore;
          bestCandidate = cleaned;
        }

        if (cleanedScore >= 45) {
          console.log(`[GENERATE] ✓ Success on attempt ${attemptIdx + 1}, time: ${Date.now() - startTime}ms`);
          return cleaned;
        }

        // If output is mostly prompt echo, try recovering residual text from the raw generation.
        const recovered = this.recoverTextFromRaw(String(raw || ''), a.prompt);
        console.log(`[GENERATE] After recover (${recovered.length} chars):`, recovered.substring(0, 150));

        const recoveredScore = this.scoreReplyCandidate(userText, recovered);
        console.log(`[GENERATE] Recovered score: ${recoveredScore}`);

        if (recoveredScore > bestScore) {
          bestScore = recoveredScore;
          bestCandidate = recovered;
        }

        if (recoveredScore >= 45) {
          console.log(`[GENERATE] ✓ Success via recovery on attempt ${attemptIdx + 1}, time: ${Date.now() - startTime}ms`);
          return recovered;
        }
      } catch (e) {
        console.error(`[GENERATE] Attempt ${attemptIdx + 1} failed:`, e);
      }
    }

    if (bestCandidate && this.shouldAcceptBestCandidate(userText, bestCandidate, bestScore)) {
      console.log(`[GENERATE] Returning best candidate after all attempts, score=${bestScore}, time: ${Date.now() - startTime}ms`);
      return bestCandidate;
    }

    console.log(`[GENERATE] ✗ All attempts failed, time: ${Date.now() - startTime}ms, returning empty`);
    return '';
  }

  // Sanitize AI reply: remove consecutive duplicate sentences and cap length
  private sanitizeReply(text: string): string {
    try {
      if (!text) return text;
      text = this.stripInstructionArtifacts(text);
      // limit extreme length
      if (text.length > 10000) text = text.slice(0, 10000) + '...';
      // split into sentence-like units (keep punctuation)
      const parts = text.split(/([.!?]+)/);
      const sentences: string[] = [];
      for (let i = 0; i < parts.length; i += 2) {
        const frag = (parts[i] || '').trim();
        const punct = (parts[i + 1] || '').trim();
        const s = (frag + (punct ? punct : '')).trim();
        if (!s) continue;
        if (sentences.length === 0 || sentences[sentences.length - 1] !== s) sentences.push(s);
      }
      let out = sentences.join(' ');
      // Fallback: if splitting produced nothing, collapse repeated whitespace
      if (!out) out = text.replace(/\s+/g, ' ').trim();
      // cap final length
      if (out.length > 4000) out = out.slice(0, 4000) + '...';
      out = this.stripInstructionArtifacts(out);
      return out;
    } catch (e) {
      console.warn('sanitizeReply error', e);
      return text;
    }
  }

  private stripInstructionArtifacts(text: string): string {
    let out = text || '';
    const artifacts = [
      /(?:^|\s)context\s*:\s*/gi,
      /(?:^|\s)contesto\s*:\s*/gi,
      /(?:^|\s)conversation\s*:\s*/gi,
      /(?:^|\s)domanda\s*:\s*/gi,
      /(?:^|\s)risposta\s*:\s*/gi,
      /(?:^|\s)risposta breve in italiano\s*:\s*/gi,
      /(?:^|\s)risposta in italiano\s*:\s*/gi,
      /(?:^|\s)assistant\s*:\s*/gi,
      /(?:^|\s)user\s*:\s*/gi,
      /(?:^|\s)system\s*:\s*/gi
    ];
    for (const rx of artifacts) out = out.replace(rx, ' ');
    return out.replace(/\s+/g, ' ').trim();
  }

  private scoreReplyCandidate(userText: string, text: string): number {
    const t = (text || '').trim();
    if (!t) {
      console.log('[QUALITY] Empty text, heavy penalty');
      return -100;
    }

    if (this.isPlaceholderLikeReply(t)) {
      console.log('[QUALITY] Placeholder tag reply, heavy penalty:', t);
      return -90;
    }

    const lowered = t.toLowerCase();
    let score = 0;

    if (/^(assistant|system|user)\s*:/i.test(lowered)) {
      console.log('[QUALITY] Starts with prompt marker, penalty');
      score -= 35;
    }

    if (t.length < 20 && /(risposta|italiano|domanda)/i.test(lowered)) {
      console.log('[QUALITY] Short + structural words, penalty (len:', t.length, ')');
      score -= 35;
    }

    // Reject clearly noisy web-like dumps (common failure mode with small generative models).
    const urlMatches = lowered.match(/https?:\/\//g) || [];
    const fakeUrlMatches = lowered.match(/\b(?:www\.|http:\/|https:\/)\S+/g) || [];
    if (urlMatches.length >= 2 || fakeUrlMatches.length >= 3) {
      console.log('[QUALITY] Too many URL-like tokens, heavy penalty');
      score -= 50;
    }

    const shortWords = lowered.split(/\s+/).filter(Boolean);
    if (t.length <= 20 && shortWords.length <= 2) {
      console.log('[QUALITY] Too short/minimal reply, penalty (len:', t.length, ', words:', shortWords.length, ')');
      score -= 30;
    }

    const words = lowered.split(/\s+/).filter(Boolean);
    if (words.length >= 10) {
      const uniq = new Set(words);
      const uniqRatio = uniq.size / words.length;
      console.log('[QUALITY] Unique ratio:', (uniqRatio * 100).toFixed(1), '% (', uniq.size, 'unique of', words.length, 'total)');
      if (uniqRatio < 0.35) {
        console.log('[QUALITY] Too repetitive, penalty');
        score -= 40;
      }
    }

    if (t.length >= 32) score += 18;
    else if (t.length >= 16) score += 8;
    else score -= 8;

    if (/[.!?]/.test(t)) score += 6;
    if (words.length >= 6 && words.length <= 45) score += 8;

    const domainAnchors = /(alessandro|developer|team lead|teamlead|esperienz|progett|competenz|lavor|profilo|tecnolog)/i;
    const asksProfileInfo = /(alessandro|profilo|esperienz|progett|lavor|competenz|chi\s+e|parlami\s+di)/i.test(userText);
    const hasDomainAnchor = domainAnchors.test(t);
    if (asksProfileInfo && !hasDomainAnchor) {
      console.log('[QUALITY] Missing profile/domain anchors for a profile question, penalty');
      score -= 32;
    }

    const tokenMatches = lowered.match(/[a-zàèéìòù]+/gi) || [];
    if (tokenMatches.length >= 10) {
      const longTokens = tokenMatches.filter((w) => w.length >= 12).length;
      const longRatio = longTokens / tokenMatches.length;
      if (longRatio >= 0.28 && !hasDomainAnchor) {
        console.log('[QUALITY] High ratio of unusually long tokens without domain anchors, penalty:', longRatio.toFixed(2));
        score -= 24;
      }
    }

    const queryTerms = this.normalizeForSearch(userText).split(' ').filter((term) => term.length >= 4);
    if (queryTerms.length > 0) {
      const hits = queryTerms.filter((term) => lowered.includes(term)).length;
      score += Math.min(18, hits * 6);

      // For non-greeting requests, require at least minimal lexical overlap with user intent.
      if (!this.isSimpleGreetingOrSmalltalk(userText) && hits === 0) {
        console.log('[QUALITY] No overlap with user terms, penalty');
        score -= 24;
      }
    }

    const kbSnippet = this.buildKnowledgeSnippet(userText, 2, 220);
    if (kbSnippet) {
      const kbTerms = this.normalizeForSearch(kbSnippet).split(' ').filter((term) => term.length >= 5);
      const kbHits = kbTerms.filter((term) => lowered.includes(term)).length;
      score += Math.min(16, kbHits * 2);
    }

    if (/\balessandro\b/.test(lowered) && /\bcome stai\b/.test(lowered)) score -= 18;
    if (/\b(ciao|salve|buongiorno|buonasera)\b/.test(lowered) && words.length >= 6) score += 4;

    // Penalize odd punctuation/noise ratio.
    const noiseChars = (t.match(/[|`~^_#{}<>\\]/g) || []).length;
    if (noiseChars >= 3) {
      console.log('[QUALITY] High noise punctuation count, penalty');
      score -= 16;
    }

    const unusualLatinChars = (t.match(/[åäöüßøæœ]/gi) || []).length;
    if (unusualLatinChars >= 2) {
      console.log('[QUALITY] Unusual latin characters for IT profile context, penalty');
      score -= 28;
    }

    if (/\([a-z]{2,6}-\d+\)/i.test(t)) {
      console.log('[QUALITY] Synthetic marker pattern detected, penalty');
      score -= 22;
    }

    console.log('[QUALITY] Candidate score', score, 'for text length', t.length);
    return score;
  }

  private shouldAcceptBestCandidate(userText: string, candidate: string, score: number): boolean {
    if (!candidate || !candidate.trim()) return false;
    if (score < 42) return false;

    const q = this.normalizeForSearch(userText);
    const asksProfileInfo = /\b(alessandro|profilo|esperienza|competenze|progetti|lavoro|contatto|linkedin|universita|laurea|team\s*lead|leadership)\b/.test(q);

    if (!asksProfileInfo) return true;

    // For profile-like requests we require stronger grounding before returning model text.
    return this.isProfileGroundedReply(candidate);
  }

  private isProfileGroundedReply(text: string): boolean {
    const normalized = this.normalizeForSearch(text || '');
    if (!normalized) return false;

    if (/[åäöüßøæœ]/i.test(text || '')) return false;
    if (/\([a-z]{2,6}-\d+\)/i.test(text || '')) return false;

    const domainTerms = [
      'alessandro', 'bompadre', 'team lead', 'leadership', 'analyst', 'developer',
      'front end', 'backend', 'database', 'etl', 'angular', 'java', 'gft',
      'universita', 'pisa', 'laurea', 'linkedin', 'email', 'progetto', 'esperienza', 'competenze'
    ];

    const hits = domainTerms.filter((term) => normalized.includes(term)).length;
    return hits >= 2;
  }

  private isSimpleGreetingOrSmalltalk(userText: string): boolean {
    const q = this.normalizeForSearch(userText);
    if (!q) return false;

    const onlyShort = q.split(' ').filter(Boolean).length <= 4;
    const greeting = /\b(ciao|salve|buongiorno|buonasera|hey|hello)\b/.test(q);
    const lightSmalltalk = /\b(come va|come stai|tutto bene|ciao mondo)\b/.test(q);
    const asksProfile = /\b(alessandro|profilo|esperienza|competenze|progetti|lavoro|contatto|linkedin)\b/.test(q);

    return (greeting || lightSmalltalk) && onlyShort && !asksProfile;
  }

  private isPlaceholderLikeReply(text: string): boolean {
    const t = (text || '').trim();
    if (!t) return true;

    // Single or repeated bracket-tags only, e.g. [profilo] or [profilo] [team].
    if (/^(?:\[[^\]\n]{1,40}\]\s*)+$/i.test(t)) return true;

    // Structural-only content inside brackets without an actual sentence.
    if (/^\[[^\]]+\]\s*[,:;\-]*\s*$/i.test(t)) return true;

    return false;
  }

  private recoverTextFromRaw(raw: string, promptUsed: string): string {
    let out = raw || '';
    if (!out) return '';

    const idx = out.lastIndexOf(promptUsed);
    if (idx >= 0) out = out.slice(idx + promptUsed.length);
    out = this.stripInstructionArtifacts(out);
    out = out.replace(/^[:\-\s"']+/, '').trim();
    return out;
  }

  // Build a compact instruction + memory prompt for small in-browser models.
  private buildPrompt(userText: string): string {
    const lang = this.detectResponseLanguage(userText);
    const lines: string[] = [];

    if (lang === 'en') {
      lines.push('You are the assistant of Alessandro Bompadre\'s personal website.');
      lines.push('You are speaking with a website visitor, not with Alessandro.');
      lines.push('Reply in natural English with a human and concrete tone.');
      lines.push('Write 2 or 3 useful sentences, without labels like System, User, or Assistant.');
      lines.push('If the visitor greets you, greet back in one sentence and add one useful profile detail.');
      lines.push('If context is missing, use only the profile data below and do not invent facts.');
      lines.push('Base profile: Alessandro is an Analyst Developer and is growing toward a Team Lead role.');
    } else {
      lines.push('Sei l\'assistente del sito personale di Alessandro Bompadre.');
      lines.push('Parli con un visitatore del sito, non con Alessandro.');
      lines.push('Rispondi in italiano naturale, con tono umano e concreto.');
      lines.push('Scrivi 2 o 3 frasi utili, senza etichette come System, User o Assistant.');
      lines.push('Se il visitatore saluta, ricambia in una frase e aggiungi subito un\'informazione utile sul profilo di Alessandro.');
      lines.push('Se manca contesto, usa solo i dati del profilo qui sotto senza inventare.');
      lines.push('Profilo base: Alessandro e un Analyst Developer e punta a crescere verso un ruolo da Team Lead.');
    }

    const kbSnippet = this.buildKnowledgeSnippet(userText, 3, 520, lang);
    if (kbSnippet) {
      lines.push(lang === 'en' ? 'Relevant profile information: ' + kbSnippet : 'Informazioni rilevanti sul profilo: ' + kbSnippet);
    }

    const history = this.getRecentConversation(4);
    if (history.length > 0) {
      lines.push(lang === 'en'
        ? 'Recent conversation context: ' + this.buildConversationContext(history)
        : 'Contesto recente della conversazione: ' + this.buildConversationContext(history));
    }

    lines.push(lang === 'en' ? 'Visitor request: ' + userText : 'Richiesta del visitatore: ' + userText);
    lines.push(lang === 'en' ? 'Natural answer:' : 'Risposta naturale:');
    return lines.join('\n');
  }

  private buildGenerationAttempts(userText: string): Array<{
    prompt: string;
    maxNewTokens: number;
    doSample: boolean;
    temperature: number;
    topP: number;
    repetitionPenalty: number;
  }> {
    const lang = this.detectResponseLanguage(userText);
    const kbSnippet = this.buildKnowledgeSnippet(userText, 3, 520, lang);
    const compactPrompt = lang === 'en'
      ? [
          'Reply as the assistant of Alessandro Bompadre\'s website.',
          'Talk to the visitor in natural English.',
          'Avoid labels and prompt artifacts.',
          kbSnippet ? 'Useful profile details: ' + kbSnippet : '',
          'Visitor question: ' + userText,
          'Answer:'
        ].filter(Boolean).join('\n')
      : [
          'Rispondi come assistente del sito di Alessandro Bompadre.',
          'Parla al visitatore in italiano naturale.',
          'Evita etichette o testo di prompt.',
          kbSnippet ? 'Profilo utile: ' + kbSnippet : '',
          'Domanda del visitatore: ' + userText,
          'Risposta:'
        ].filter(Boolean).join('\n');

    const guidedPrompt = lang === 'en'
      ? [
          'Visitor: ' + userText,
          'Website assistant of Alessandro: reply in English with 2 useful and specific sentences.',
          kbSnippet ? 'Profile details: ' + kbSnippet : '',
          'Text:'
        ].filter(Boolean).join('\n')
      : [
          'Visitatore: ' + userText,
          'Assistente del sito di Alessandro: rispondi in italiano con 2 frasi utili e specifiche.',
          kbSnippet ? 'Dettagli profilo: ' + kbSnippet : '',
          'Testo:'
        ].filter(Boolean).join('\n');

    return [
      { prompt: this.buildPrompt(userText), maxNewTokens: 96, doSample: true, temperature: 0.72, topP: 0.9, repetitionPenalty: 1.12 },
      { prompt: compactPrompt, maxNewTokens: 72, doSample: true, temperature: 0.65, topP: 0.88, repetitionPenalty: 1.15 },
      { prompt: guidedPrompt, maxNewTokens: 64, doSample: false, temperature: 0.2, topP: 0.95, repetitionPenalty: 1.18 }
    ];
  }

  private buildConversationContext(history: Array<{from: 'user' | 'bot', text: string}>): string {
    return history
      .slice(-4)
      .map((m) => {
        const text = (m.text || '').replace(/\s+/g, ' ').trim();
        const short = text.length > 120 ? text.slice(0, 120) + '...' : text;
        return (m.from === 'user' ? 'utente: ' : 'assistente: ') + short;
      })
      .join(' | ');
  }

  private async loadKnowledgeBase(): Promise<void> {
    try {
      const candidateUrls = [
        this.kbPath,
        '/' + this.kbPath,
        './' + this.kbPath,
        new URL(this.kbPath, document.baseURI).toString()
      ];

      let raw: any = null;
      let loadedFrom = '';
      const failures: string[] = [];

      for (const url of candidateUrls) {
        try {
          const resp = await fetch(url, { cache: 'no-cache' });
          if (!resp.ok) {
            failures.push(url + ' -> HTTP ' + resp.status);
            continue;
          }
          raw = await resp.json();
          loadedFrom = url;
          break;
        } catch (err: any) {
          const msg = err && err.message ? err.message : String(err);
          failures.push(url + ' -> ' + msg);
        }
      }

      if (raw === null) {
        throw new Error('KB fetch failed. Attempts: ' + failures.join(' | '));
      }

      const flattened: KnowledgeEntry[] = [];

      if (Array.isArray(raw)) {
        for (const x of raw) {
          if (!x || typeof x.id !== 'string' || typeof x.topic !== 'string' || typeof x.text !== 'string') continue;
          const textIt = String(x.text || '').trim();
          const textEn = String((x as any).englishText || textIt).trim();
          flattened.push({
            id: x.id,
            topic: x.topic,
            text: textIt || textEn,
            textIt,
            textEn,
            keywords: Array.isArray(x.keywords)
              ? x.keywords.map((k: any) => String(k || '').toLowerCase().trim()).filter(Boolean)
              : []
          });
        }
      } else if (raw && typeof raw === 'object') {
        const profile = Array.isArray((raw as any).profile) ? (raw as any).profile : [];
        for (const p of profile) {
          if (!p || (typeof p.text !== 'string' && typeof p.englishText !== 'string')) continue;
          const textIt = String(p.text || '').trim();
          const textEn = String(p.englishText || textIt).trim();
          flattened.push({
            id: String(p.id || ('profile-' + flattened.length)),
            topic: String(p.topic || 'profilo'),
            text: textIt || textEn,
            textIt,
            textEn,
            keywords: Array.isArray(p.keywords)
              ? p.keywords.map((k: any) => String(k || '').toLowerCase().trim()).filter(Boolean)
              : []
          });
        }

        const jobs = Array.isArray((raw as any).jobs) ? (raw as any).jobs : [];
        for (let i = 0; i < jobs.length; i++) {
          const j = jobs[i] || {};
          const title = String(j.title || '').trim();
          const company = String(j.company || '').trim();
          const period = String(j.period || '').trim();
          const descIt = String(j.description_it || '').trim();
          const descEn = String(j.description_en || '').trim();
          const baseLabel = title && company ? `${title} in ${company}` : title || company;
          const textIt = [baseLabel, period ? `(${period})` : '', descIt || descEn]
            .filter(Boolean)
            .join(' ')
            .trim();
          const textEn = [baseLabel, period ? `(${period})` : '', descEn || descIt]
            .filter(Boolean)
            .join(' ')
            .trim();
          if (!textIt && !textEn) continue;

          const skillKeywords = Array.isArray(j.skills)
            ? j.skills.map((s: any) => String(s || '').toLowerCase().trim()).filter(Boolean)
            : [];

          flattened.push({
            id: `job-${i}-${company || 'entry'}`,
            topic: 'esperienza professionale',
            text: textIt || textEn,
            textIt,
            textEn,
            keywords: [title, company, 'esperienza', 'lavoro', 'experience', 'career', ...skillKeywords]
              .map((k) => String(k || '').toLowerCase().trim())
              .filter(Boolean)
          });
        }

        const educations = Array.isArray((raw as any).educations) ? (raw as any).educations : [];
        for (let i = 0; i < educations.length; i++) {
          const e = educations[i] || {};
          const institution = String(e.institution || '').trim();
          const degree = String(e.degree || '').trim();
          const year = String(e.year || '').trim();
          const grade = String(e.grade || '').trim();
          const textIt = [
            institution ? `Formazione presso ${institution}.` : '',
            degree ? `Titolo: ${degree}.` : '',
            year ? `Anno/periodo: ${year}.` : '',
            grade ? `Valutazione: ${grade}.` : ''
          ].filter(Boolean).join(' ').trim();
          const textEn = [
            institution ? `Education at ${institution}.` : '',
            degree ? `Degree: ${degree}.` : '',
            year ? `Year/period: ${year}.` : '',
            grade ? `Grade: ${grade}.` : ''
          ].filter(Boolean).join(' ').trim();
          if (!textIt && !textEn) continue;

          const eduSkills = Array.isArray(e.skills)
            ? e.skills.map((s: any) => String(s || '').toLowerCase().trim()).filter(Boolean)
            : [];

          flattened.push({
            id: `edu-${i}-${institution || 'entry'}`,
            topic: 'formazione',
            text: textIt || textEn,
            textIt,
            textEn,
            keywords: [institution, degree, 'universita', 'laurea', 'formazione', 'education', 'degree', ...eduSkills]
              .map((k) => String(k || '').toLowerCase().trim())
              .filter(Boolean)
          });
        }
      } else {
        throw new Error('KB format invalid: expected array or object');
      }

      this.knowledgeBase = flattened.filter((x) => !!x.text && !!x.topic);

      this.kbLoaded = this.knowledgeBase.length > 0;
      console.log('[KB] Loaded entries:', this.knowledgeBase.length, 'from:', loadedFrom);
    } catch (e) {
      this.kbLoaded = false;
      this.knowledgeBase = [];
      console.warn('[KB] Load failed, continuing without KB', e);
    }
  }

  private buildKnowledgeSnippet(userText: string, maxItems = 3, maxChars = 520, lang?: 'it' | 'en'): string {
    if (!this.kbLoaded || this.knowledgeBase.length === 0) return '';
    const q = this.normalizeForSearch(userText);
    if (!q) return '';

    const chosenLang = lang || this.detectResponseLanguage(userText);
    const scored = this.knowledgeBase
      .map((entry) => ({ entry, score: this.scoreKnowledgeEntry(q, entry) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.max(1, maxItems));

    if (scored.length === 0) return '';

    let out = '';
    for (const s of scored) {
      const piece = this.formatKnowledgeSnippetPiece(s.entry.topic, this.getKnowledgeEntryText(s.entry, chosenLang));
      const next = out ? out + ' ' + piece : piece;
      if (next.length > maxChars) break;
      out = next;
    }

    if (!out) {
      out = this.formatKnowledgeSnippetPiece(scored[0].entry.topic, this.getKnowledgeEntryText(scored[0].entry, chosenLang)).slice(0, maxChars);
    }
    return out;
  }

  private formatKnowledgeSnippetPiece(topic: string, text: string): string {
    const cleanTopic = String(topic || '')
      .replace(/[\[\]_:.-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return cleanTopic ? cleanTopic + ': ' + text : text;
  }

  private scoreKnowledgeEntry(query: string, entry: KnowledgeEntry): number {
    let score = 0;
    const topic = this.normalizeForSearch(entry.topic);
    const textSource = [entry.text, entry.textIt, entry.textEn].filter(Boolean).join(' ');
    const text = this.normalizeForSearch(textSource);

    if (topic && query.includes(topic)) score += 3;
    if (topic && topic.includes(query)) score += 2;

    for (const kw of entry.keywords || []) {
      const k = this.normalizeForSearch(kw);
      if (!k) continue;
      if (query.includes(k)) score += 4;
      else if (k.length >= 4 && query.indexOf(k.slice(0, 4)) >= 0) score += 1;
    }

    const queryTerms = query.split(' ').filter((t) => t.length >= 3);
    for (const term of queryTerms) {
      if (text.includes(term)) score += 1;
    }

    return score;
  }

  private normalizeForSearch(text: string): string {
    return String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private detectResponseLanguage(userText: string): 'it' | 'en' {
    const q = this.normalizeForSearch(userText || '');
    if (!q) return this.translate.getLang();

    const englishSignals = /\b(hello|hi|hey|good\s+morning|good\s+evening|who\s+is|tell\s+me|about|experience|skills|projects|contact|email|linkedin|education|university|degree|team\s*lead|leadership)\b/.test(q);
    const italianSignals = /\b(ciao|salve|buongiorno|buonasera|chi\s+e|parlami|esperienz|competenz|progett|contatt|formazione|universita|laurea)\b/.test(q);

    if (englishSignals && !italianSignals) return 'en';
    if (italianSignals && !englishSignals) return 'it';
    return this.translate.getLang();
  }

  private getKnowledgeEntryText(entry: KnowledgeEntry, lang: 'it' | 'en'): string {
    if (lang === 'en') return String(entry.textEn || entry.text || '').trim();
    return String(entry.textIt || entry.text || '').trim();
  }

  private getRecentConversation(maxItems: number): Array<{from: 'user' | 'bot', text: string}> {
    const all = this.messages
      .filter((m) => !!m && (m.from === 'user' || m.from === 'bot') && !!(m.text || '').trim())
      .slice(-Math.max(0, maxItems));
    return all;
  }

  private buildLocalFallbackReply(userText: string): string {
    const t = (userText || '').toLowerCase();
    const normalized = this.normalizeForSearch(userText || '');
    const lang = this.detectResponseLanguage(userText || '');

    // For profile-oriented questions, prefer a deterministic KB-grounded answer.
    if (/\b(alessandro|profilo|chi\s+e|parlami\s+di|parlami\s+di\s+te|chi\s+sei|esperienz|competenz|progett|lavor|team\s*lead|leadership|contatt|email|linkedin|universita|laurea|formazione|studi|pisa|who\s+is|tell\s+me|experience|skills|projects|contact|education|degree)/.test(normalized)) {
      const kbReply = this.buildKnowledgeGroundedFallback(userText || '');
      if (kbReply) return kbReply;
    }

    if (/\b(ciao|salve|buongiorno|buonasera|hello|hi|hey)\b/.test(t)) {
      return lang === 'en'
        ? 'Hi! I can quickly explain Alessandro\'s skills, experience, and professional goals.'
        : 'Ciao! Posso raccontarti competenze, esperienza e obiettivi professionali di Alessandro in modo rapido e chiaro.';
    }
    if (/\b(team lead|leadership|guida|team|manager|responsabil)\b/.test(t)) {
      return lang === 'en'
        ? 'Alessandro is moving toward a Team Lead role, with focus on team guidance, operational support, and people growth.'
        : 'Alessandro punta a un ruolo da Team Lead: focus su guida del team, supporto operativo e crescita delle persone.';
    }
    if (/\b(esperienz|progett|svilupp|analyst|developer|competen|experience|project|skills)\b/.test(t)) {
      return lang === 'en'
        ? 'He has an Analyst Developer profile with solid technical experience and strong quality focus, oriented to concrete team outcomes.'
        : 'Ha un profilo Analyst Developer con esperienza tecnica e attenzione alla qualità, orientato a risultati concreti per il team.';
    }
    if (/\b(contatt|email|linkedin|colloquio|meeting|contact|reach)\b/.test(t)) {
      return lang === 'en'
        ? 'You can reach him via LinkedIn or email from the website: if you want, I can suggest the best channel for your request.'
        : 'Puoi contattarlo tramite LinkedIn o email dal sito: se vuoi, ti indico il canale migliore in base alla tua richiesta.';
    }

    const compact = (userText || '').replace(/\s+/g, ' ').trim();
    const short = compact.length > 80 ? compact.slice(0, 80) + '...' : compact;
    if (short) {
      return lang === 'en'
        ? 'I got your request: "' + short + '". I can give you a more precise answer if you want a focus on technical experience, leadership, or contacts.'
        : 'Ho capito la tua richiesta: "' + short + '". Posso darti una risposta più precisa se mi dici se vuoi focus su esperienza tecnica, leadership o contatti.';
    }

    return lang === 'en'
      ? 'I can help with Alessandro\'s profile, skills, experience, and professional goals.'
      : this.botReply;
  }

  private buildKnowledgeGroundedFallback(userText: string): string {
    if (!this.kbLoaded || this.knowledgeBase.length === 0) return '';

    const normalized = this.normalizeForSearch(userText || '');
    const lang = this.detectResponseLanguage(userText || '');
    const intent = this.detectKnowledgeIntent(normalized);
    const selected = this.selectKnowledgeEntriesForIntent(intent, normalized);

    let parts: string[] = selected
      .map((e) => this.getKnowledgeEntryText(e, lang))
      .map((v) => String(v || '').trim())
      .filter(Boolean);

    // If intent selection was too weak, fallback to score-based snippet extraction.
    if (parts.length === 0) {
      const snippet = this.buildKnowledgeSnippet(userText, 3, 620, lang);
      if (!snippet) return '';

      parts = snippet
        .split(/\s+(?=[a-z0-9\s]{2,30}:\s)/i)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 3)
        .map((piece) => {
          const colon = piece.indexOf(':');
          if (colon <= 0) return piece;
          const text = piece.slice(colon + 1).trim();
          return text || piece;
        });
    }

    const unique: string[] = [];
    for (const p of parts) {
      if (!unique.includes(p)) unique.push(p);
    }

    const reply = unique.join(' ').replace(/\s+/g, ' ').trim();
    if (!reply) return '';

    return reply;
  }

  private detectKnowledgeIntent(normalizedText: string): 'contacts' | 'leadership' | 'technical' | 'profile' | 'generic' {
    if (/\b(contatt|email|linkedin|colloquio|meeting|raggiung|scriv|contact|reach)/.test(normalizedText)) {
      return 'contacts';
    }
    if (/\b(team\s*lead|leadership|guida|manager|responsabil|coordin)/.test(normalizedText)) {
      return 'leadership';
    }
    if (/\b(esperienz|tecnic|competenz|svilupp|frontend|back\s*end|database|etl|analyst|developer|experience|skills|project)/.test(normalizedText)) {
      return 'technical';
    }
    if (/\b(parlami\s+di\s+te|chi\s+sei|chi\s+e|profilo|alessandro|presentazione|universita|laurea|formazione|studi|pisa|who\s+is|tell\s+me|about|education|degree)/.test(normalizedText)) {
      return 'profile';
    }
    return 'generic';
  }

  private selectKnowledgeEntriesForIntent(
    intent: 'contacts' | 'leadership' | 'technical' | 'profile' | 'generic',
    normalizedText: string
  ): KnowledgeEntry[] {
    const topicNorm = (v: string) => this.normalizeForSearch(v || '');
    const byTopic = (rx: RegExp) => this.knowledgeBase.filter((e) => rx.test(topicNorm(e.topic)));

    let preferred: KnowledgeEntry[] = [];

    if (intent === 'contacts') {
      preferred = [...byTopic(/\bcontatt/), ...this.rankKnowledgeEntries(normalizedText)];
    } else if (intent === 'leadership') {
      preferred = [...byTopic(/\bleadership/), ...byTopic(/\bprofilo/), ...byTopic(/stile\s+di\s+lavor/), ...this.rankKnowledgeEntries(normalizedText)];
    } else if (intent === 'technical') {
      preferred = [...byTopic(/esperienza\s+tecnica/), ...byTopic(/\bprofilo/), ...this.rankKnowledgeEntries(normalizedText)];
    } else if (intent === 'profile') {
      preferred = [...byTopic(/\bprofilo/), ...byTopic(/esperienza\s+tecnica/), ...byTopic(/\bleadership/), ...this.rankKnowledgeEntries(normalizedText)];
    } else {
      preferred = this.rankKnowledgeEntries(normalizedText);
    }

    const unique: KnowledgeEntry[] = [];
    const seen = new Set<string>();
    for (const entry of preferred) {
      const key = entry.id || entry.topic || entry.text;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(entry);
      if (unique.length >= 3) break;
    }

    return unique;
  }

  private rankKnowledgeEntries(normalizedText: string): KnowledgeEntry[] {
    return this.knowledgeBase
      .map((entry) => ({ entry, score: this.scoreKnowledgeEntry(normalizedText, entry) }))
      .sort((a, b) => b.score - a.score)
      .map((x) => x.entry);
  }

  // Remove prompt echo and keep only the assistant continuation.
  private extractAssistantReply(raw: string, promptUsed?: string): string {
    if (!raw) return '';
    let out = raw;
    console.log('[EXTRACT] Input length:', raw.length, 'first 100 chars:', raw.substring(0, 100));

    // Remove full-prompt echo if present.
    if (promptUsed) {
      if (out.startsWith(promptUsed)) {
        console.log('[EXTRACT] Prompt at start, removing');
        out = out.slice(promptUsed.length);
      } else {
        const pIdx = out.lastIndexOf(promptUsed);
        if (pIdx >= 0) {
          console.log('[EXTRACT] Prompt at index', pIdx, ', removing');
          out = out.slice(pIdx + promptUsed.length);
        }
      }
    }

    const assistantMarkers = ['Assistant:', 'Risposta:', 'Testo:', 'Risposta naturale:'];
    for (const marker of assistantMarkers) {
      const idx = out.lastIndexOf(marker);
      if (idx >= 0) {
        console.log('[EXTRACT] Found marker', marker, 'at index', idx, ', keeping after');
        out = out.slice(idx + marker.length);
      }
    }

    // Stop if model starts a new synthetic turn.
    const stopMarkers = ['\nUser:', '\nSystem:', '\nConversation:', '\nDomanda:', '\nRisposta:'];
    for (const marker of stopMarkers) {
      const markerIdx = out.indexOf(marker);
      if (markerIdx >= 0) {
        console.log('[EXTRACT] Found stop marker', marker, 'at index', markerIdx);
        out = out.slice(0, markerIdx);
        break;
      }
    }
    out = out.replace(/^[:\-\s"']+/, '').trim();
    out = this.stripInstructionArtifacts(out);
    console.log('[EXTRACT] Final output length:', out.length, 'content:', out.substring(0, 150));
    return out;
  }

  // Query AI backend. Expects JSON { reply: string } or plain text response.
  private async queryAI(message: string): Promise<string> {
    try {
      const resp = await fetch(this.aiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message })
      });
      if (!resp.ok) throw new Error('AI endpoint error: ' + resp.status);
      // try parse json
      const ct = resp.headers.get('content-type') || '';
      if (ct.indexOf('application/json') !== -1) {
        const j = await resp.json();
        if (j && typeof j.reply === 'string') return j.reply;
        // fallback to string coercion
        return String(j || '');
      }
      // otherwise return plain text
      const txt = await resp.text();
      return txt || '';
    } catch (e) {
      console.warn('queryAI failed', e);
      throw e;
    }
  }
}
